export type BackendSource = "localhost" | "shared-dev";

const SKIP_LOCAL_AUTO_SWITCH_KEY = "m360.configuration-skip-local-auto-switch";
const LOCAL_UNAVAILABLE_NOTICE_KEY = "m360.backend-source.local-unavailable-notice";
const LOCAL_UNAVAILABLE_DETAIL_KEY = "m360.backend-source.local-unavailable-detail";

const isBackendSource = (value: unknown): value is BackendSource => value === "localhost" || value === "shared-dev";

function getBackendSourceStorageKey(): string {
  const surface = import.meta.env.MODE.startsWith("portal") ? "portal" : "staff";
  return `m360.backend-source.${surface}`;
}

function getSessionLocalhostKey(): string {
  return `${getBackendSourceStorageKey()}.session-localhost`;
}

function discardDurableLocalhostPreference(): void {
  try {
    const stored = window.localStorage.getItem(getBackendSourceStorageKey());
    if (!stored) return;

    const parsed: unknown = JSON.parse(stored);
    if (parsed === "localhost") {
      window.localStorage.removeItem(getBackendSourceStorageKey());
    }
  } catch {
    // Best-effort migration of sticky localhost from older sessions.
  }
}

function readDurableBackendSource(): BackendSource | null {
  discardDurableLocalhostPreference();

  try {
    const stored = window.localStorage.getItem(getBackendSourceStorageKey());
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    return isBackendSource(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function hasSessionLocalhost(): boolean {
  try {
    return window.sessionStorage.getItem(getSessionLocalhostKey()) === "1";
  } catch {
    return false;
  }
}

export function readBackendSource(): BackendSource | null {
  if (hasSessionLocalhost()) return "localhost";
  return readDurableBackendSource();
}

export function persistBackendSource(source: BackendSource): void {
  try {
    if (source === "localhost") {
      window.sessionStorage.setItem(getSessionLocalhostKey(), "1");
      discardDurableLocalhostPreference();
      return;
    }

    window.sessionStorage.removeItem(getSessionLocalhostKey());
    window.localStorage.setItem(getBackendSourceStorageKey(), JSON.stringify(source));
  } catch {
    // Preference is best-effort; a failed write leaves the prior stored source in place after reload.
  }
}

export function hasLocalBackendBootstrapFailure(): boolean {
  try {
    return window.sessionStorage.getItem(SKIP_LOCAL_AUTO_SWITCH_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberLocalBackendBootstrapFailure(): void {
  try {
    window.sessionStorage.setItem(SKIP_LOCAL_AUTO_SWITCH_KEY, "1");
  } catch {
    // Best-effort; a failed write still lets Configuration auto-switch back to localhost.
  }
}

export function clearLocalBackendBootstrapFailure(): void {
  try {
    window.sessionStorage.removeItem(SKIP_LOCAL_AUTO_SWITCH_KEY);
  } catch {
    // Best-effort.
  }
}

export function hasLocalBackendUnavailableNotice(): boolean {
  try {
    return window.sessionStorage.getItem(LOCAL_UNAVAILABLE_NOTICE_KEY) === "1";
  } catch {
    return false;
  }
}

type LocalBackendUnavailableNoticeListener = () => void;

const localBackendUnavailableNoticeListeners = new Set<LocalBackendUnavailableNoticeListener>();

function notifyLocalBackendUnavailableNoticeListeners(): void {
  for (const listener of localBackendUnavailableNoticeListeners) {
    listener();
  }
}

export function subscribeLocalBackendUnavailableNotice(listener: LocalBackendUnavailableNoticeListener): () => void {
  localBackendUnavailableNoticeListeners.add(listener);
  return () => {
    localBackendUnavailableNoticeListeners.delete(listener);
  };
}

export function rememberLocalBackendUnavailableNotice(detail?: string): void {
  try {
    window.sessionStorage.setItem(LOCAL_UNAVAILABLE_NOTICE_KEY, "1");
    if (detail) {
      window.sessionStorage.setItem(LOCAL_UNAVAILABLE_DETAIL_KEY, detail);
    } else {
      window.sessionStorage.removeItem(LOCAL_UNAVAILABLE_DETAIL_KEY);
    }
  } catch {
    // Best-effort; missing notice still leaves the user on shared-dev.
  }
  notifyLocalBackendUnavailableNoticeListeners();
}

export function readLocalBackendUnavailableDetail(): string | null {
  try {
    return window.sessionStorage.getItem(LOCAL_UNAVAILABLE_DETAIL_KEY);
  } catch {
    return null;
  }
}

export function clearLocalBackendUnavailableNotice(): void {
  try {
    window.sessionStorage.removeItem(LOCAL_UNAVAILABLE_NOTICE_KEY);
    window.sessionStorage.removeItem(LOCAL_UNAVAILABLE_DETAIL_KEY);
  } catch {
    // Best-effort.
  }
  notifyLocalBackendUnavailableNoticeListeners();
}

export function applyBackendSource(source: BackendSource): void {
  persistBackendSource(source);
  if (source === "localhost") {
    clearLocalBackendBootstrapFailure();
    clearLocalBackendUnavailableNotice();
  }
  window.location.reload();
}

export function fallBackToSharedDevBackend(error?: Error): void {
  rememberLocalBackendBootstrapFailure();
  rememberLocalBackendUnavailableNotice(error?.message);
  applyBackendSource("shared-dev");
}
